import React, { RefObject, forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { PopperPlacementType, TooltipProps } from "@mui/material";
import { CustomIconButton } from "..";
import DropDown from "../../drop-down/DropDown";
import CustomButton from "../custom-button/CustomButton";
import { CustomTooltip } from "../../tooltip";
import { usePrevious } from "../../../hooks";

type SvgIconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;
type IconSource = IconProp | SvgIconComponent;

type ButtonSize = "xs" | "sm" | "md" | "lg";

type ButtonColor = "primary" | "secondary";

type ButtonVariant = "text" | "outlined" | "contained";

export interface CustomMenuButtonHandle {
	onClose: () => void;
}

interface CustomMenuButtonProps {
	btnClassName?: string;
	children: React.ReactElement;
	className?: string;
	color?: ButtonColor;
	disabled?: boolean;
	disablePortal?: boolean;
	endIcon?: IconSource;
	forceClose?: boolean;
	forceOpenSelector?: boolean;
	fullWidth?: boolean;
	icon?: IconSource;
	id?: string;
	iconClassName?: string;
	iconColor?: string;
	label?: string;
	position?: PopperPlacementType;
	size?: ButtonSize;
	startIcon?: IconSource;
	text?: string;
	tooltip?: TooltipProps["title"];
	variant?: ButtonVariant;
}

const CustomMenuButton = forwardRef<CustomMenuButtonHandle, CustomMenuButtonProps>(
	(
		{
			btnClassName = "",
			children,
			className = "",
			color = "secondary",
			disabled,
			disablePortal = false,
			endIcon = undefined,
			forceClose = false,
			forceOpenSelector = false,
			fullWidth = false,
			icon = undefined,
			id = "",
			iconClassName = "",
			iconColor = "inherit",
			label = "",
			position = "bottom-start",
			size = "md",
			startIcon = undefined,
			text = "",
			tooltip = "",
			variant = "outlined",
		},
		ref
	) => {
		const anchorRef = useRef(null);
		const [open, setOpen] = useState(false);
		const prevForceOpenSelector = usePrevious(forceOpenSelector);
		useEffect(() => {
			if (open && (forceClose || (!forceOpenSelector && prevForceOpenSelector !== forceOpenSelector))) {
				setOpen(false);
			}
		}, [open, forceClose, prevForceOpenSelector, forceOpenSelector]);

		const handleOpen = () => {
			setOpen(!open);
		};
		const handleClose = useCallback(() => {
			if (!forceOpenSelector) {
				setOpen(false);
			}
		}, [forceOpenSelector]);

		useEffect(() => {
			if (disabled) {
				handleClose();
			}
		}, [disabled, handleClose]);

		useEffect(() => {
			if (!ref) {
				return undefined;
			}
			(ref as RefObject<CustomMenuButtonHandle>).current = { onClose: handleClose };
			return () => {
				if (!ref) {
					return;
				}
				(ref as RefObject<CustomMenuButtonHandle | null>).current = null;
			};
		}, [handleClose, ref]);

		return (
			<>
				{text ? (
					<CustomTooltip title={tooltip}>
						<CustomButton
							ref={anchorRef}
							className={className}
							color={color}
							disabled={disabled}
							endIcon={endIcon}
							fullWidth={fullWidth}
							id={id}
							size={size}
							startIcon={startIcon}
							variant={variant}
							onClick={handleOpen}
						>
							{text}
						</CustomButton>
					</CustomTooltip>
				) : (
					<CustomIconButton
						ref={anchorRef}
						btnClassName={btnClassName}
						className={className}
						color={color}
						disabled={disabled}
						icon={icon}
						iconClassName={iconClassName}
						iconColor={iconColor}
						id={id}
						label={label}
						size={size}
						tooltip={tooltip}
						variant={variant}
						onClick={handleOpen}
					/>
				)}
				<DropDown
					ref={anchorRef}
					disablePortal={disablePortal}
					open={open}
					position={position}
					onClose={handleClose}
				>
					{children}
				</DropDown>
			</>
		);
	}
);

export default CustomMenuButton;
